import {
  faDownload,
  faMagnifyingGlass,
  faPlus,
  faSliders,
  faTableColumns,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams, useRouteMatch } from 'react-router-dom';

import { Breadcrumbs, Button, Input, PageHeader, Table, toast, Toolbar } from '@feathr/components';

import creativeColumns from '../columns/creativeColumns';
import formColumns from '../columns/formColumns';
import templateColumns from '../columns/templateColumns';
import CreativeRowActions from '../components/CreativeRowActions';
import FormRowActions from '../components/FormRowActions';
import PrototypeDoneModal from '../components/PrototypeDoneModal';
import TemplateRowActions from '../components/TemplateRowActions';
import type { ISampleCreative, ISampleForm, ISampleTemplate } from '../sampleData';
import { sampleCreatives, sampleForms, sampleTemplates } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';

import * as shellStyles from '../Shell/PrototypeShell.css';

const CONTENT_TABS = ['templates', 'creatives', 'forms'] as const;
type TContentTab = (typeof CONTENT_TABS)[number];

function isContentTab(value: string | undefined): value is TContentTab {
  return CONTENT_TABS.includes(value as TContentTab);
}

function getTitle(t: TFunction, tab: TContentTab): string {
  const titles: Record<TContentTab, string> = {
    templates: t('Templates'),
    creatives: t('Creatives'),
    forms: t('Forms'),
  };
  return titles[tab];
}

function getCreateLabel(t: TFunction, tab: TContentTab): string {
  const labels: Record<TContentTab, string> = {
    templates: t('New template'),
    creatives: t('Upload creative'),
    forms: t('New form'),
  };
  return labels[tab];
}

function ContentPage(): JSX.Element {
  const { t } = useTranslation();
  const { tab: tabParam } = useParams<{ tab: string }>();

  if (!isContentTab(tabParam)) {
    return <Redirect to={'../templates'} />;
  }

  return <ContentTabPage t={t} tab={tabParam} />;
}

interface IContentTabPageProps {
  t: TFunction;
  tab: TContentTab;
}

function ContentTabPage({ t, tab }: Readonly<IContentTabPageProps>): JSX.Element {
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Content'), defaultPath: '/content/templates' },
    getTitle(t, tab),
    `/content/${tab}`,
  );

  const lower = search.trim().toLowerCase();

  const templateCols = useMemo(
    () =>
      templateColumns({
        base,
        t,
        onAction: (template, action): void => {
          if (action === 'duplicate') {
            toast(t('Duplicated "{{name}}".', { name: template.name }), { type: 'success' });
            return;
          }
          setDoneOpen(true);
        },
        renderActions: (template, onAction): JSX.Element => (
          <TemplateRowActions onAction={onAction} template={template} />
        ),
      }),
    [base, t],
  );

  const creativeCols = useMemo(
    () =>
      creativeColumns({
        base,
        t,
        onAction: (creative, action): void => {
          if (action === 'restore') {
            toast(t('Restored "{{name}}".', { name: creative.name }), { type: 'success' });
            return;
          }
          if (action === 'archive') {
            toast(t('Archived "{{name}}".', { name: creative.name }), { type: 'success' });
            return;
          }
          setDoneOpen(true);
        },
        renderActions: (creative, onAction): JSX.Element => (
          <CreativeRowActions creative={creative} onAction={onAction} />
        ),
      }),
    [base, t],
  );

  const formCols = useMemo(
    () =>
      formColumns({
        base,
        t,
        onAction: (form, action): void => {
          if (action === 'duplicate') {
            toast(t('Duplicated "{{name}}".', { name: form.name }), { type: 'success' });
            return;
          }
          setDoneOpen(true);
        },
        renderActions: (form, onAction): JSX.Element => (
          <FormRowActions form={form} onAction={onAction} />
        ),
      }),
    [base, t],
  );

  const filteredTemplates = useMemo<ISampleTemplate[]>(
    () =>
      lower
        ? sampleTemplates.filter((tmpl) => tmpl.name.toLowerCase().includes(lower))
        : sampleTemplates,
    [lower],
  );

  const filteredCreatives = useMemo<ISampleCreative[]>(
    () =>
      lower
        ? sampleCreatives.filter((cr) => cr.name.toLowerCase().includes(lower))
        : sampleCreatives,
    [lower],
  );

  const filteredForms = useMemo<ISampleForm[]>(
    () => (lower ? sampleForms.filter((f) => f.name.toLowerCase().includes(lower)) : sampleForms),
    [lower],
  );

  const filterElements = [
    <Box key={'search'} w={320}>
      <Input
        isClearable={true}
        onChange={(next?: string): void => setSearch(next ?? '')}
        placeholder={t('Search by name…')}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        type={'text'}
        value={search}
      />
    </Box>,
    <Toolbar align={'right'} key={'filters'}>
      <Button prefix={<FontAwesomeIcon icon={faSliders} />} type={'secondary'}>
        {t('Filters')}
      </Button>
      <Button prefix={<FontAwesomeIcon icon={faTableColumns} />} type={'secondary'}>
        {t('Customize columns')}
      </Button>
    </Toolbar>,
  ];

  const actions = (
    <Toolbar>
      <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
        {t('Export as CSV')}
      </Button>
      <Button
        onClick={(): void => setDoneOpen(true)}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {getCreateLabel(t, tab)}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader
        actions={actions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={getTitle(t, tab)}
      />
      <div className={shellStyles.body}>
        {tab === 'templates' && (
          <Table<ISampleTemplate>
            columns={templateCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredTemplates}
          />
        )}
        {tab === 'creatives' && (
          <Table<ISampleCreative>
            columns={creativeCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredCreatives}
          />
        )}
        {tab === 'forms' && (
          <Table<ISampleForm>
            columns={formCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredForms}
          />
        )}
      </div>

      <PrototypeDoneModal
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={getCreateLabel(t, tab)}
      />
    </>
  );
}

export default ContentPage;
